import { useEffect, useState } from "react";

import useMediaQuery from "~/hec/hooks/useMediaQuery";
import { shouldRevealBackToTop } from "~/lib/ui";

/**
 * Back-to-top button (SFCC-11673). Port of the SFRA org_hec global footer
 * control: a fixed bottom-right pill that reveals once the user scrolls past
 * 20% of the page (see `shouldRevealBackToTop`) and smooth-scrolls to the top.
 * Mounted globally in `layout.tsx` (all non-checkout/non-TPA pages, all sites),
 * matching SFRA where it lives in `pageFooter.isml`.
 */
export function BackToTop() {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      setVisible(
        shouldRevealBackToTop(el.scrollTop, el.scrollHeight, el.clientHeight),
      );
    };
    onScroll(); // account for a restored scroll position on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Initial (and SSR) state is hidden, so server and first client render agree.
  if (!visible) return null;

  return (
    <button
      type="button"
      data-testid="back-to-top-button"
      aria-label="Back to top"
      title="Back to top"
      onClick={() =>
        window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" })
      }
      // `back-to-top` is a hook for app.css: when a bottom sticky bar (PDP ATC,
      // cart checkout) is mounted, the pill is lifted above it together with
      // the Zendesk launcher — see `body:has(.sticky-atc-bar.fixed)` rules.
      className="back-to-top bottom-17 group fixed right-[20px] z-40 flex h-10 w-12  items-center justify-center gap-1 rounded-l-lg bg-[#F2F2F2] text-[#142745] transition-colors hover:bg-[#142745] hover:text-white"
    >
      {/* SFRA chevron geometry. Shown at rest; swapped for the "Top" label on
          hover/focus (stroke=currentColor flips white via the button's hover). */}
      <svg
        width="14"
        height="8"
        viewBox="0 0 20 11"
        fill="none"
        aria-hidden="true"
        className="group-hover:hidden group-focus-visible:hidden"
      >
        <path
          d="M1.515 10 10 1.515 18.485 10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <span className="hidden text-xs font-semibold uppercase group-hover:inline group-focus-visible:inline">
        Top
      </span>
    </button>
  );
}
